export type AgencyStatus = "trial" | "active" | "past_due" | "suspended";
/** Hierarchy (top → bottom):
 *    shareholder → executive_manager → operations_manager → lead_broker → broker → member
 *  ``shareholder`` is the shareholder/apex tier — a strict superset of
 *  executive_manager (sees + manages everyone, all pages, every owner power)
 *  that is additionally immune (only shareholders manage shareholders), never
 *  measured (excluded from all stats), and hidden from every non-shareholder.
 *  ``operations_manager`` is the executive manager's right hand: full agency
 *  visibility, can manage everyone except the executive manager, but cannot
 *  perform executive-only ops (billing, promote-to-operations_manager). */
export type UserRole =
  | "shareholder"
  | "executive_manager"
  | "operations_manager"
  | "lead_broker"
  | "broker"
  | "member";

/** Human-readable role labels — the single source of truth for how a role
 *  renders in the UI. Every badge / dropdown / label should route through this
 *  (or {@link roleLabel}) rather than printing the raw key. */
export const ROLE_LABEL: Record<UserRole, string> = {
  shareholder: "Shareholder",
  executive_manager: "Executive Manager",
  operations_manager: "Operations Manager",
  lead_broker: "Lead Broker",
  broker: "Broker",
  member: "Member",
};

/** Safe label lookup — falls back to the raw value for unknown keys. */
export function roleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return ROLE_LABEL[role as UserRole] ?? role;
}

/** Market search + map: strategic surfaces, narrower than Hot Offers.
 *  Executive + operations manager only — these views aggregate the entire
 *  scraped universe and host the share-link generator. */
export const MARKET_ROLES: readonly UserRole[] = [
  "shareholder",
  "executive_manager",
  "operations_manager",
];
/** Deals (and other strategic CRM surfaces) — executive + operations manager only. */
export const CRM_ROLES: readonly UserRole[] = [
  "shareholder",
  "executive_manager",
  "operations_manager",
];
/** Clients — every agent in the agency, with per-role visibility scoping
 *  enforced server-side: executive / operations manager see everyone, lead
 *  broker sees self + direct reports, broker / member see only their own. So
 *  the page is open to all roles but each one sees a different slice. */
export const CLIENTS_ROLES: readonly UserRole[] = [
  "shareholder",
  "executive_manager",
  "operations_manager",
  "lead_broker",
  "broker",
  "member",
];
export type UserStatus = "active" | "disabled";
export type Platform = "myhome" | "ss";

export interface Agency {
  id: string;
  name: string;
  status: AgencyStatus;
  trial_ends_at: string | null;
  seat_limit: number;
}

export interface CurrentUser {
  id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  status: UserStatus;
  last_login_at: string | null;
  /** Most recent WebSocket heartbeat. Null if the user has never opened
   *  the messenger. UI uses this for "last seen X ago" copy. */
  last_seen_at: string | null;
  /** True iff the backend has at least one open WS connection for this
   *  user right now. Snapshot at fetch time — not pushed live. */
  is_online: boolean;
  avatar_url: string | null;
  /** True = the avatar is pinned by an admin. The Profile page hides the
   *  picker and PATCH /api/auth/me rejects avatar changes with 403. */
  avatar_locked: boolean;
  /** Per-user sidebar order — list of stable nav keys (route paths).
   *  Null = use the default order; the Layout filters role-invisible
   *  items and appends new ones to the end. */
  nav_order: string[] | null;
  /** Per-user Fresh-page filter selection, remembered across sessions/devices.
   *  Opaque blob owned by the Fresh page. Null = no saved filters. */
  fresh_filters: Record<string, unknown> | null;
  /** Saved Telegram chat id for fresh-listing alerts — set once, reused by
   *  every alert. Null = never set; the alert dialog asks for it inline. */
  telegram_chat_id: string | null;
  /** Resolved page keys the current user is allowed to see, under the
   *  agency's role-page overrides. Backend is the source of truth;
   *  frontend uses it for nav filtering + route guards. */
  allowed_pages: string[];
  agency: Agency;
}

export interface StoredCredential {
  platform: Platform;
  email: string;
  has_phone: boolean;
  updated_at: string;
}

export interface AgencyListItem {
  id: string;
  name: string;
  status: AgencyStatus;
  trial_ends_at: string | null;
  seat_limit: number;
  user_count: number;
  created_at: string;
}

export interface AgencyDetail extends AgencyListItem {
  users: Array<{
    id: string;
    email: string;
    full_name: string | null;
    role: UserRole;
    status: UserStatus;
    last_login_at: string | null;
  }>;
  recent_jobs: Array<{
    id: string;
    source_url: string;
    status: "pending" | "running" | "done" | "error";
    started_at: string;
    finished_at: string | null;
    error_message: string | null;
  }>;
}

export interface PipelineEvent {
  step: string;
  status?: "start" | "progress" | "done" | "error";
  message?: string;
  result?: any;
}

export interface TeamMember {
  id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  status: UserStatus;
  last_login_at: string | null;
  /** Most recent WS heartbeat. UI shows "last seen 5m ago" once the
   *  user goes offline. */
  last_seen_at: string | null;
  /** Most recent *real* interaction (mouse/keyboard) — drives "active Xm ago"
   *  and the away/offline distinction. */
  last_active_at: string | null;
  /** Authentic presence: active (interacting) / away (tab open, idle) /
   *  offline. Prefer this over ``is_online`` for presence dots. */
  presence_status: "active" | "away" | "offline";
  /** True iff the user has at least one open WS connection (an idle open tab
   *  reads True). Legacy — use ``presence_status`` for honest presence. */
  is_online: boolean;
  /** Denormalised "first manager" — kept for legacy displays. `managers` is
   *  the authoritative full set (an agent can have several). */
  manager_id: string | null;
  manager_name: string | null;
  /** All managers who can see/manage this user (all equal, no primary). */
  managers: { id: string; name: string | null }[];
  placement_count: number;
  /** Distinct placements (deduped by job_run_id) this user published today
   *  in UTC. Used by the bulk-publish agent picker to surface workload. */
  placements_today: number;
  avatar_url: string | null;
  /** Optional voice / rules feed for the bulk-publish Gemini prompt. Editable
   *  on the Team page by anyone who can manage this user. */
  publish_persona: string | null;
  /** True iff a MyHome credential row exists for this user. The bulk-publish
   *  picker uses this to grey out / warn agents that can't actually be
   *  assigned. The flag is a presence check — the encrypted secret is
   *  never returned. */
  has_myhome_creds: boolean;
  /** True iff the MyHome credential additionally has a phone number — the
   *  bulk-publish endpoint requires it (the portal rejects agent-published
   *  listings without one). */
  has_myhome_phone: boolean;
  /** True iff an SS.ge credential row exists. SS publishing is opt-in and
   *  per-agent, so the picker uses this to dim the SS toggle's affordance
   *  for agents who don't have it. */
  has_ss_creds: boolean;
  created_at: string;
}

/** Live wallet balance for one portal. ``ok`` is false when there are no
 *  saved credentials or the portal login / balance fetch failed; ``error``
 *  carries a short code the UI maps to a human message. */
export interface PortalBalance {
  ok: boolean;
  balance: number | null;
  currency: string | null;
  error: string | null;
}

/** Both portals' balances for one user, as returned by
 *  ``GET /api/team/{id}/portal-balances`` (and the batch list variant). */
export interface PortalBalances {
  user_id: string;
  myhome: PortalBalance;
  ss: PortalBalance;
}

/** One portal's saved-credential state for a member, in the Vault roster. */
export interface VaultPlatformState {
  connected: boolean;
  email: string | null;
  has_phone: boolean;
  updated_at: string | null;
}

/** A member row in the Vault (`GET /api/vault/members`). ``can_manage`` is
 *  false for members ranked above the viewer — those rows are view-only. */
export interface VaultMember {
  user_id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar_url: string | null;
  can_manage: boolean;
  myhome: VaultPlatformState;
  ss: VaultPlatformState;
}

/** Decrypted credential, returned only by the audited reveal endpoint
 *  (`POST /api/vault/members/{id}/credentials/{platform}/reveal`). */
export interface VaultReveal {
  platform: Platform;
  email: string;
  password: string;
  phone: string | null;
}

/** One day of the broker scorecard's monthly placement chart. */
export interface BenchmarkDayPoint {
  date: string;
  count: number;
  hit_goal: boolean;
}

// ── Analytics → Benchmarks ────────────────────────────────────────────

export interface AgencyBenchmarkRollup {
  placements_total: number;
  placement_goal_daily: number;
  placements_expected: number;
  days_hit_goal: number;
  broker_days: number;
  deals_done: number;
  deals_lost: number;
  deal_goal_monthly: number;
  conversion_rate: number | null;
  sale_count: number;
  rent_count: number;
  commission_value: string;
}

export interface BenchmarkDailyPoint {
  date: string;
  placements: number;
  deals_done: number;
  deals_lost: number;
  brokers_hit_goal: number;
  active_brokers: number;
}

export interface BenchmarkBrokerRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  role: UserRole;
  avatar_url: string | null;
  placements: number;
  days_hit_goal: number;
  days_active: number;
  deals_done: number;
  deals_lost: number;
  conversion_rate: number | null;
  sale_count: number;
  rent_count: number;
  commission_value: string;
  best_day_count: number;
  best_day_date: string | null;
}

export interface BenchmarkHeatmapCell {
  user_id: string;
  date: string;
  placements: number;
  hit_goal: boolean;
}

export interface BenchmarkDayOfWeek {
  dow: number;        // 1=Mon .. 7=Sun
  label: string;
  placements: number;
  deals_closed: number;
}

export interface BenchmarkStageCount {
  stage: DealStage;
  count: number;
}

export interface AnalyticsBenchmarks {
  window_start: string;
  window_end: string;
  days_in_window: number;
  visible_broker_count: number;
  agency: AgencyBenchmarkRollup;
  daily: BenchmarkDailyPoint[];
  by_broker: BenchmarkBrokerRow[];
  heatmap: BenchmarkHeatmapCell[];
  by_dow: BenchmarkDayOfWeek[];
  deal_stages: BenchmarkStageCount[];
  /** Commission figures are USD-normalised at each deal's close-date rate.
   *  False when a closed deal had no FX rate and was omitted. */
  commission_fx_complete: boolean;
}

/** Per-agency benchmark targets — tuned by an owner on the Team page. */
export interface AgencyGoals {
  placement_goal_daily: number;
  deal_goal_monthly: number;
}

/** Per-broker scorecard. ``placements_today`` is the raw row count
 *  (publishing one source to MyHome + SS = 2); ``deals_this_month``
 *  counts closed deals (done OR lost) in the current Tbilisi calendar
 *  month. Goals come from the agency settings. */
export interface BrokerBenchmarks {
  user_id: string;
  full_name: string | null;
  email: string | null;
  role: UserRole;
  avatar_url: string | null;
  placements_today: number;
  placement_goal_daily: number;
  placements_this_month: number;
  placement_days_hit: number;
  placement_days_elapsed: number;
  placement_days_in_month: number;
  /** Per-day chart data for the current Tbilisi calendar month.
   *  Empty in the leaderboard endpoint response. */
  placement_days: BenchmarkDayPoint[];
  deals_this_month: number;
  deals_done: number;
  deals_lost: number;
  deal_goal_monthly: number;
  month_start: string;
  month_end: string;
}

export type ScheduleOffReason =
  | "day_off"
  | "vacation"
  | "sick"
  | "holiday"
  | "other";

export interface ScheduleDay {
  date: string;
  is_working: boolean;
  /** Where the state came from: an explicit date exception, the broker's
   *  recurring weekly pattern, or the opt-out default (working). */
  source: "exception" | "pattern" | "default";
  off_reason: ScheduleOffReason | null;
  note: string | null;
}

export interface BrokerWeek {
  user_id: string;
  full_name: string | null;
  email: string | null;
  role: UserRole;
  avatar_url: string | null;
  has_pattern: boolean;
  working_weekdays: number[] | null;
  off_reason: ScheduleOffReason | null;
  /** Exactly 7 entries, Mon..Sun. */
  days: ScheduleDay[];
  can_manage: boolean;
}

export interface ScheduleWeek {
  week_start: string;
  week_end: string;
  week_offset: number;
  is_current_week: boolean;
  brokers: BrokerWeek[];
}

export interface SchedulePattern {
  user_id: string;
  has_pattern: boolean;
  working_weekdays: number[] | null;
  off_reason: ScheduleOffReason | null;
}

export type ScheduleAuditAction =
  | "pattern_set"
  | "pattern_cleared"
  | "exception_set"
  | "exception_cleared";

export interface ScheduleAuditEntry {
  id: string;
  action: ScheduleAuditAction;
  day: string | null;
  created_at: string;
  actor_user_id: string | null;
  actor_name: string | null;
  target_user_id: string | null;
  /** Self-contained before/after snapshot (names + values). */
  detail: Record<string, unknown>;
}

export interface TeamSession {
  id: string;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  expires_at: string;
  current: boolean;
}

export interface Invite {
  id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  manager_id: string | null;
  expires_at: string;
  accept_url: string;
  created_at: string;
}

export interface InvitePublic {
  agency_name: string;
  role: UserRole;
  email: string;
  full_name: string | null;
  inviter_name: string | null;
  expires_at: string;
}

export interface PlacementUser {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
}

export interface OnboardingStep {
  id: string;
  label: string;
  done: boolean;
  cta_label: string | null;
  cta_href: string | null;
  cta_external: boolean;
}

export interface OnboardingState {
  steps: OnboardingStep[];
  completed: boolean;
}
export type ClientStatus = "active" | "paused" | "closed";
export type DealStage =
  | "planning"
  | "booked"
  | "decision_pending"
  | "done"
  | "lost";

export const DEAL_STAGES: readonly DealStage[] = [
  "planning",
  "booked",
  "decision_pending",
  "done",
  "lost",
];

export interface ClientUserRef {
  id: string;
  full_name: string | null;
  email: string | null;
  /** Optional — populated for refs from endpoints that bother to join
   *  the user table (deals.assigned_user / deals.created_by). Older
   *  callers leave it undefined; the Avatar component falls back to
   *  initials in that case. */
  avatar_url?: string | null;
}

export interface Client {
  id: string;
  agency_id: string;
  assigned_user_id: string | null;
  assigned_user: ClientUserRef | null;
  full_name: string;
  /** Canonical E.164 phone — what the dialer / SMS gateway should use.
   *  ``phone_raw`` is whatever the broker actually typed; UI shows the
   *  normalized form but reveals the raw input on hover when the two
   *  differ, so a botched parse can be spotted immediately. */
  phone: string | null;
  phone_raw: string | null;
  email: string | null;
  source: string | null;
  notes: string | null;
  status: ClientStatus;
  deal_type: "sale" | "rent" | null;
  rooms_min: number | null;
  rooms_max: number | null;
  bedrooms_min: number | null;
  bedrooms_max: number | null;
  floor_min: number | null;
  floor_max: number | null;
  price_min: string | null;
  price_max: string | null;
  currency: "USD" | "GEL" | "EUR" | null;
  area_min: string | null;
  area_max: string | null;
  property_types: string[] | null;
  districts: string[] | null;
  // Catalog street IDs (uuid strings). When set, the matcher restricts
  // listings to those whose ``street`` text contains any of the picked
  // streets' normalized names.
  streets: string[] | null;
  building_statuses: string[] | null;
  conditions: string[] | null;
  // 2026-04-28 — match against the structured fields the refresh-
  // structured pass now populates reliably across both portals.
  bathrooms_min: number | null;
  bathrooms_max: number | null;
  project_types: string[] | null;
  year_built_min: number | null;
  year_built_max: number | null;
  required_features: string[] | null;
  required_amenities: string[] | null;
  preferred_features: string[] | null;
  preferred_amenities: string[] | null;
  // Geographic anchor for proximity matching. All three must be set
  // for the matcher to apply the radius filter.
  center_lat: string | null;
  center_lon: string | null;
  radius_km: string | null;
  new_match_count: number;
  created_at: string;
  updated_at: string;
}

/** Building-age vocabulary — canonical key → Georgian label. Keep in
 *  lockstep with BUILDING_STATUS_VALUES in app/schemas/client.py and
 *  _MH_STATUS_TO_KEY in main.py. */
export const BUILDING_STATUS_LABELS: Record<string, string> = {
  new_built: "ახალი აშენებული",
  old_built: "ძველი აშენებული",
  under_construction: "მშენებარე",
};

/** Renovation-state vocabulary — canonical key → Georgian label. */
export const CONDITION_LABELS: Record<string, string> = {
  new_renovation: "ახალი გარემონტებული",
  old_renovation: "ძველი რემონტით",
  needs_renovation: "სარემონტო",
  green_frame: "მწვანე კარკასი",
  white_frame: "თეთრი კარკასი",
  black_frame: "შავი კარკასი",
};

/** Building-project type vocabulary — same canonical codes the ingest
 *  layer uses for both MyHome ``project_type_id`` and SS.ge ``project``
 *  text label. */
export const PROJECT_TYPE_LABELS: Record<string, string> = {
  non_standard: "არასტანდარტული",
  kavlashvili: "ყავლაშვილის",
  leningrad: "ლენინგრადის",
  lvov: "ლვოვის",
  kiev: "კიევის",
  tbilisi_yard: "თბილისური ეზო",
  moscow: "მოსკოვის",
  urban: "ქალაქური",
  czech: "ჩეხური",
  khrushchev: "ხრუშჩოვის",
  tukhareli: "თუხარელის",
  vedzisi: "ვედზისი",
  yugoslavia: "იუგოსლავიის",
  metromsheni: "მეტრომშენი",
};

/** Building-feature vocabulary (lifestyle / building extras). */
export const FEATURE_LABELS: Record<string, string> = {
  balcony: "Balcony",
  loggia: "Loggia",
  basement: "Basement",
  comfortable: "Comfortable",
  garage: "Garage",
  pet_friendly: "Pet-friendly",
  urgent: "Urgent",
  security_alarm: "Security alarm",
  storage: "Storage",
  view_street: "View on street",
  view_yard: "View on yard",
  remote_viewing: "Remote viewing",
  has_3d: "3D tour",
  spa: "Spa",
  bar: "Bar",
  gym: "Gym",
  jacuzzi: "Jacuzzi",
  sauna: "Sauna",
  guard: "Guard",
  concierge: "Concierge",
  barrier_gate: "Barrier gate",
  fire_system: "Fire system",
};

/** In-unit amenities (utilities, kitchen / furniture flags). */
export const AMENITY_LABELS: Record<string, string> = {
  air_conditioning: "Air conditioning",
  cable_tv: "Cable TV",
  drinking_water: "Drinking water",
  electricity: "Electricity",
  elevator: "Elevator",
  fridge: "Fridge",
  furniture: "Furniture",
  glazed_windows: "Glazed windows",
  heating: "Heating",
  hot_water: "Hot water",
  internet: "Internet",
  iron_door: "Iron door",
  natural_gas: "Natural gas",
  sewage: "Sewage",
  telephone: "Telephone",
  tv: "TV",
  washing_machine: "Washing machine",
  water: "Water",
  wifi: "Wi-Fi",
  built_in_kitchen: "Built-in kitchen",
  pool: "Pool",
  ventilation: "Ventilation",
  bed: "Bed",
  sofa: "Sofa",
  table: "Table",
  chairs: "Chairs",
  stove: "Stove",
  oven: "Oven",
};

export interface ClientListResponse {
  items: Client[];
  total: number;
  limit: number;
  offset: number;
}

/** Per-broker row in ClientFunnel.per_broker. */
export interface ClientFunnelBrokerRow {
  user_id: string;
  name: string;
  active_clients: number;
  deals_started_30d: number;
  won_30d: number;
  /** ``won_30d / deals_started_30d`` — null when the broker started no
   *  deals in the window. UI shows "—" instead of misleading 0%. */
  conversion_rate: number | null;
}

/** Aggregate client → deal funnel for the viewer's scope. Backend is
 *  visible-user scoped (broker → own pipeline, manager → +reports,
 *  owner/director → whole agency). */
export interface ClientFunnel {
  active_clients: number;
  clients_with_deal: number;
  clients_in_pipeline: number;
  clients_won_30d: number;
  /** Active clients ≥14d old with no deal — the neglected pile. */
  stale_clients: number;
  avg_days_client_to_first_deal: number | null;
  avg_days_booked_to_done: number | null;
  per_broker: ClientFunnelBrokerRow[];
}

export interface DealClientRef {
  id: string;
  full_name: string;
  phone: string | null;
}

export interface Deal {
  id: string;
  agency_id: string;
  assigned_user_id: string | null;
  assigned_user: ClientUserRef | null;
  /** Reporter — the user who logged the deal. Set server-side on POST
   *  from the caller; read-only thereafter (PATCH ignores it). Null for
   *  legacy rows that predate the column. */
  created_by_user_id: string | null;
  created_by: ClientUserRef | null;
  client_id: string | null;
  client: DealClientRef | null;
  hot_offer_id: string | null;
  placement_id: string | null;
  stage: DealStage;
  deal_type: "sale" | "rent" | null;
  /** ISO date string (YYYY-MM-DD) — required, settable by the broker. */
  deal_date: string;
  property_title: string | null;
  property_url: string | null;
  price: string | null;
  currency: string | null;
  /** Seller / property-owner's contact, distinct from ``client.phone``
   *  (the buyer). Pre-filled from the source HotOffer when the deal is
   *  created from one; null when the broker hasn't entered it. */
  owner_phone: string | null;
  commission_pct: string | null;
  commission_amount: string | null;
  planning_at: string | null;
  booked_at: string | null;
  decision_pending_at: string | null;
  done_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  notes: string | null;
  /** Soonest trip still ahead for this deal; once every logged trip is in
   *  the past it falls back to the most recent one, so a card the broker
   *  already left for keeps showing the time (rendered in a past tint
   *  rather than hidden). Null when nothing has been logged. */
  next_departure: DealDeparture | null;
  /** How many trips are logged in total — drives the card's "+2" hint. */
  departure_count: number;
  created_at: string;
  updated_at: string;
}

/** One logged trip for a deal — "leaving the office at 15:00".
 *
 *  Distinct from the stage timestamps: ``booked_at`` is when the card was
 *  dragged into Booked, ``leave_at`` is a real appointment and usually
 *  points at the future. A deal collects several of these. */
export interface DealDeparture {
  id: string;
  deal_id: string;
  user_id: string | null;
  /** Who is going — null once the account is deleted (the row survives). */
  user: ClientUserRef | null;
  leave_at: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface DealDepartureListResponse {
  items: DealDeparture[];
  total: number;
}

/** A row of the "out today" strip — a departure plus enough of its parent
 *  deal to render the line without a second round-trip. */
export interface DealDepartureDayItem extends DealDeparture {
  deal: {
    id: string;
    property_title: string | null;
    stage: DealStage;
  };
}

/** ``GET /api/deals/departures/today``. ``now`` is the server's instant —
 *  the strip splits departed from upcoming against it rather than against
 *  a browser clock that may be minutes off. */
export interface DealDepartureDay {
  day: string;
  now: string;
  items: DealDepartureDayItem[];
}

export interface DealListResponse {
  items: Deal[];
  total: number;
  limit: number;
  offset: number;
}

/** One row of a Deal's activity stream. Separate from ``Deal.notes`` —
 *  notes is a curated context blob the broker rewrites; comments are
 *  chronological updates ("owner agreed to meet Fri 14:00", etc.). The
 *  Edit-Deal panel renders the stream below the field grid. */
export interface DealCommentAuthor {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url?: string | null;
}

export interface DealComment {
  id: string;
  deal_id: string;
  user_id: string | null;
  author: DealCommentAuthor | null;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface DealCommentListResponse {
  items: DealComment[];
  total: number;
}

export interface DealStageBucket {
  stage: DealStage;
  count: number;
  items: Deal[];
}

/** Currency-aware commission rollup. ``by_currency`` is the exact native
 *  breakdown; ``approx_gel`` / ``approx_usd`` are that sum converted to a
 *  single currency via the latest NBG rates (null when the FX cache is cold
 *  or a bucket can't be converted). */
export interface CommissionTotals {
  by_currency: { currency: string; total: string }[];
  approx_gel: string | null;
  approx_usd: string | null;
}

export interface DealBoard {
  stages: DealStageBucket[];
  done_count: number;
  done_value: CommissionTotals;
}

/** A saved fresh-listing alert: filter rules + a Telegram chat it pings when
 *  new matching listings appear. ``rules`` mirrors the Fresh filter state. */
export interface FreshAlert {
  id: string;
  name: string;
  telegram_chat_id: string;
  rules: Record<string, unknown>;
  enabled: boolean;
  /** Advanced every poll tick — doubles as the alert's heartbeat / "last run". */
  last_checked_at: string;
  /** When the alert last actually pushed matches to Telegram (null until first fire). */
  last_notified_at: string | null;
  /** How many matches it sent on its last fire. */
  last_match_count: number;
  created_at: string;
  updated_at: string;
}

export interface FreshAlertListResponse {
  items: FreshAlert[];
  total: number;
}

/** The user's one Telegram chat for fresh alerts, plus what the UI needs to
 *  walk them through setting it up. */
export interface FreshTelegramChat {
  /** Saved chat id; null until the user sets one. */
  chat_id: string | null;
  /** Alert bot's username (no "@") for a t.me link; null if unknown. */
  bot_username: string | null;
  /** False = FRESH_ALERT_BOT_TOKEN missing on the server; nothing is delivered. */
  bot_enabled: boolean;
  /** How many of the user's alerts a chat change rewrites. */
  alert_count: number;
}

export interface DealStageCount {
  stage: DealStage;
  count: number;
}

export interface DealBrokerStats {
  user_id: string;
  full_name: string | null;
  email: string | null;
  role: UserRole;
  open_count: number;
  done_count: number;
  lost_count: number;
  done_value: CommissionTotals;
  conversion_rate: number | null;
}

export interface DealTypeStats {
  deal_type: "sale" | "rent";
  open_count: number;
  done_count: number;
  lost_count: number;
  done_value: CommissionTotals;
}

export interface DealStats {
  window_start: string | null;
  window_end: string | null;
  open_count: number;
  done_count: number;
  lost_count: number;
  done_value: CommissionTotals;
  conversion_rate: number | null;
  avg_days_to_close: number | null;
  stages: DealStageCount[];
  brokers: DealBrokerStats[];
  /** Sale-vs-rent rollup. Closed counts honour the time window; open
   *  is a live snapshot. Contains both rows by default; collapses to one
   *  when the page filters by deal_type. */
  by_type: DealTypeStats[];
}

export interface PlacementPlatformPub {
  id: string;
  platform: Platform;
  published_url: string;
  external_id: string | null;
  // Portal soft-delete: set when removed on the portal (UI greys it).
  deleted_at?: string | null;
  portal_delete_status?: string | null;
}

export interface PlatformDeleteResult {
  placement_id: string;
  platform: string;
  ok: boolean;
  error: string | null;
}

export interface Placement {
  id: string;
  platform: Platform;
  published_url: string;
  external_id: string | null;
  title: string | null;
  /** Source-listing body text, captured at publish time. Null for manual
   *  entries and rows published before description capture. */
  description: string | null;
  price: string | null;
  currency: string | null;
  rooms: number | null;
  area_sqm: string | null;
  property_type: string | null;
  floor: number | null;
  total_floors: number | null;
  district: string | null;
  /** Free-text street name (e.g. "ი. ჭავჭავაძის გამზ."). Nullable on older
   *  rows published before street capture / on listings without one. */
  street: string | null;
  cover_photo_url: string | null;
  photo_count: number | null;
  owner_name: string | null;
  owner_phone: string | null;
  source_url: string | null;
  reference_id: string | null;
  published_at: string;
  /** Null for manual entries (logged outside the publish pipeline). */
  job_run_id: string | null;
  /** True iff the row was created via POST /api/placements rather than
   *  by the publish pipeline. UI shows a "manual" badge. */
  is_manual: boolean;
  user: PlacementUser | null;
  offer_kind: OfferKind | null;
  offer_value: string | null;
  /** Free-text broker notes — owner availability, negotiation state, etc.
   *  Internal CRM only; never surfaced on client-facing share links. */
  notes: string | null;
  /** Every platform publication in this group (job_run_id, or the row's
   *  own id for manual entries). Includes the primary placement itself
   *  — always at least one entry. */
  platforms: PlacementPlatformPub[];
}

export type OfferKind = "percent" | "fixed";

/** KPI strip on the Placements page. ``this_week_delta`` is the change vs.
 *  the prior 7 days — a positive number means "publishing more than last
 *  week"; rendered as a green ▲ on the tile. */
export interface PlacementStats {
  total: number;
  this_week: number;
  this_week_delta: number;
  myhome: number;
  ss: number;
  manual: number;
  missing_phone: number;
  missing_offer: number;
}

export interface PlacementDistrict {
  district: string;
  count: number;
}

export interface PlacementStreet {
  street: string;
  count: number;
}

export type PlacementSort =
  | "published_desc"
  | "published_asc"
  | "price_desc"
  | "price_asc";

/** Shared card-view fields returned by client-match endpoints. Both the
 *  hot-offer match (`offer`) and the placement match (`placement`) feed
 *  the same card renderer; placement-only fields are simply null on a
 *  hot-offer listing and vice versa.
 *
 *  Kept in sync with ``_MatchedOffer`` + ``_MatchedPlacement`` in
 *  ``app/routes/clients.py``. Fields added here must also be populated
 *  server-side or the card defaults to the "not available" branch. */
export interface MatchedListing {
  id: string;
  platform: Platform;
  published_url: string;
  title: string | null;
  price: string | null;
  currency: string | null;
  /** FX-normalised USD price — populated on hot offers, null on placements. */
  price_usd?: string | null;
  rooms: number | null;
  /** Bedrooms is tracked separately from total rooms on hot offers. */
  bedrooms?: number | null;
  district: string | null;
  /** Canonical neighborhood code — preferred over `district` (which is a
   *  broader admin region) when rendering the location label. */
  neighborhood?: string | null;
  cover_photo_url: string | null;
  photo_count?: number | null;
  area_sqm: string | null;
  floor: number | null;
  total_floors: number | null;
  property_type: string | null;
  deal_type: string | null;
  short_description?: string | null;
  /** Agent-posture flags — highlight cards where the owner welcomes agencies
   *  and warn when they don't. Always false for placement matches. */
  welcomes_agents?: boolean;
  forbids_agents?: boolean;
  is_exclusive?: boolean;
  owner_type?: string | null;
  /** Set when the portal calls the poster an owner but the server can show a
   *  firm posted it ("company": the portal links the account to a registered
   *  agency; "volume": the account posts at agency scale). Such rows come
   *  back with ``owner_type: "agency"``; this is the reason. */
  undeclared_agency?: "company" | "volume" | null;
  vip_level?: string | null;
  /** Freshness — ``listed_at`` for hot offers, ``published_at`` for
   *  placements. UI treats them interchangeably. */
  listed_at?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  published_at?: string | null;
  /** Deal-quality signals from ``load_benchmarks_for``. Null when no
   *  benchmark exists for the listing's cohort (e.g. land, very new
   *  neighborhoods). */
  price_per_sqm_usd?: string | null;
  price_position?: "cheap" | "average" | "expensive" | null;
  price_percent_vs_median?: number | null;
  neighborhood_median_sqm_usd?: number | null;
  neighborhood_sample_size?: number | null;
  price_benchmark_tier?: string | null;
}

export interface HotOfferMatch {
  id: string;
  score: string;
  ai_reason: string | null;
  dismissed: boolean;
  notified_at: string | null;
  created_at: string;
  offer: MatchedListing;
}

export interface PlacementMatch {
  id: string;
  score: string;
  ai_reason: string | null;
  dismissed: boolean;
  notified_at: string | null;
  created_at: string;
  placement: MatchedListing;
}

/** Share-link summary (broker-side). Matches `_ShareLinkOut` in
 *  `app/routes/clients.py`. ``status`` transitions
 *  ``preparing`` → ``ready``  (or ``failed`` if no item could be rehosted).
 *  ``language`` is the default UI locale for the public buyer page —
 *  ``ka`` (Georgian), ``ru`` (Russian), or ``en`` (English). */
export interface ClientShareLink {
  id: string;
  token: string;
  status: "preparing" | "ready" | "failed";
  language: "ka" | "ru" | "en";
  expires_at: string;
  created_at: string;
  revoked_at: string | null;
  public_url: string;
  item_count: number;
  /** Buyer view analytics (creator previews excluded). */
  view_count: number;
  last_viewed_at: string | null;
  items: Array<{
    id: string;
    kind: "placement" | "hot_offer" | "fresh" | "external";
    status: "preparing" | "ready" | "failed";
    order: number;
    title: string | null;
    open_count: number;
    last_opened_at: string | null;
    /** Portal URL this card was built from, so the manage panel can jump
     *  back to the original listing. Null on cards whose snapshot predates
     *  the field, and on manually-authored ones. */
    source_url?: string | null;
    /** Card preview for the manage panel — first rehosted photo and the
     *  headline facts from the snapshot. Absent until the prep worker has
     *  finished the card. */
    cover_photo?: string | null;
    price_usd?: string | null;
    price?: string | null;
    currency?: string | null;
    rooms?: number | null;
    area_sqm?: string | null;
    location?: string | null;
    platform?: string | null;
  }>;
}

/** Editable snapshot fields the in-app item editor writes back via
 *  `PATCH /api/share-links/items/:id`. Mirrors `_ItemUpdateIn` in
 *  `app/routes/share_links.py`. Numbers come back as strings for the
 *  decimal fields so the API round-trips them without float drift. */
export interface ShareItemSnapshot {
  title?: string | null;
  price?: string | number | null;
  currency?: string | null;
  rooms?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area_sqm?: string | number | null;
  floor?: number | null;
  total_floors?: number | null;
  year_built?: number | null;
  property_type?: string | null;
  deal_type?: string | null;
  district?: string | null;
  neighborhood?: string | null;
  street?: string | null;
  building_status?: string | null;
  condition?: string | null;
  full_description?: string | null;
  features?: string[] | null;
  amenities?: string[] | null;
  source_url?: string | null;
}

/** Single-link payload behind the edit modal. Mirrors `_ShareLinkDetailOut`
 *  in `app/routes/share_links.py` — the list endpoint deliberately omits the
 *  snapshots, so this is fetched per link on open. */
export interface ClientShareLinkDetail extends Omit<ClientShareLink, "items"> {
  agent_full_name: string | null;
  agent_email: string | null;
  agent_phone: string | null;
  items: Array<
    ClientShareLink["items"][number] & {
      source_url: string | null;
      photo_count: number;
      error_message: string | null;
      snapshot: ShareItemSnapshot;
    }
  >;
}

/** Public share-page payloads. Mirrors `_ShareLinkOut` in
 *  `app/routes/public_share.py` — fetched by the unauthenticated
 *  /share/:token route. */
export interface PublicShareAgent {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  agency_name: string | null;
}

export interface PublicShareItemSummary {
  id: string;
  kind: "placement" | "hot_offer";
  status: "preparing" | "ready" | "failed";
  order: number;
  cover_photo_url: string | null;
  title: string | null;
  price: string | null;
  currency: string | null;
  /** FX-normalised USD price — drives the "USD" toggle on the public
   *  page. Null on placements + on hot offers without an FX value. */
  price_usd?: string | null;
  rooms: number | null;
  area_sqm: string | null;
  property_type: string | null;
  deal_type: string | null;
  floor: number | null;
  total_floors: number | null;
  district: string | null;
  neighborhood: string | null;
  /** Frozen benchmark fields — populated for hot_offer items when the
   *  cohort had data at share-link creation time. Null otherwise. */
  price_position?: "cheap" | "average" | "expensive" | null;
  price_percent_vs_median?: number | null;
  /** Original listing URL. The API only sends it to a logged-in viewer
   *  from the owning agency — anonymous buyers always get null, so the
   *  presence of this field IS the "staff is looking" signal. */
  source_url?: string | null;
}

export interface PublicShareLink {
  token: string;
  status: "preparing" | "ready" | "failed";
  language: "ka" | "ru" | "en";
  expires_at: string;
  /** True when the request comes from the broker who created the link
   *  (logged in as ``link.agent_id``). Drives inline-edit affordances
   *  on the public page; always false for anonymous buyers. */
  is_editable: boolean;
  /** First name of the client the link was prepared for, surfaced
   *  in the page's headline. Null when the broker left the client
   *  full_name empty (rare). */
  client_first_name: string | null;
  agent: PublicShareAgent;
  items: PublicShareItemSummary[];
}

export interface PublicShareItemDetail {
  id: string;
  kind: "placement" | "hot_offer" | "external";
  status: "preparing" | "ready" | "failed";
  language: "ka" | "ru" | "en";
  is_editable: boolean;
  title: string | null;
  price: string | null;
  currency: string | null;
  price_usd?: string | null;
  rooms: number | null;
  /** Bedrooms / bathrooms / year_built / street are populated from
   *  HotOffer; placements leave them null. */
  bedrooms?: number | null;
  bathrooms?: number | null;
  area_sqm: string | null;
  property_type: string | null;
  deal_type: string | null;
  floor: number | null;
  total_floors: number | null;
  year_built?: number | null;
  district: string | null;
  neighborhood: string | null;
  street?: string | null;
  building_status: string | null;
  condition: string | null;
  full_description: string | null;
  short_description: string | null;
  features: string[];
  amenities: string[];
  photos: string[];
  lat: string | null;
  lon: string | null;
  /** Per-sqm price ($USD) — drives the benchmark badge tooltip. */
  price_per_sqm_usd?: string | null;
  price_position?: "cheap" | "average" | "expensive" | null;
  price_percent_vs_median?: number | null;
  neighborhood_median_sqm_usd?: number | null;
  neighborhood_sample_size?: number | null;
  /** Agency-staff only — see `PublicShareItemSummary.source_url`. */
  source_url?: string | null;
  agent: PublicShareAgent;
}

// ---------------------------------------------------------------------------
// Finance (owner/director Net-P&L + expenses). Money fields are strings —
// the backend serialises Numeric as a string to avoid float drift.
// ---------------------------------------------------------------------------

/**
 * A category slug. Categories are agency data now (`expense_categories`), so
 * this can't be a closed union — resolve a slug to its label and tint through
 * the list from `GET /api/finance/categories`, never a hardcoded map.
 */
export type ExpenseCategory = string;

/**
 * The two deal-linked payout buckets. Still hardcoded because they mirror a
 * fixed BACKEND invariant, not user data: they are seeded into every agency,
 * require a deal_id + payee_user_id, are rejected on recurring templates, and
 * can be renamed but never archived, deleted, or minted anew.
 */
export const SALARY_CATEGORIES: ExpenseCategory[] = [
  "agent_salary",
  "team_lead_salary",
];

export function isSalaryCategory(c: ExpenseCategory): boolean {
  return SALARY_CATEGORIES.includes(c);
}

/** One row of the agency's own category list. */
export interface ExpenseCategoryDef {
  id: string;
  /** Opaque and immutable — it's what's stored on every ledger row. */
  slug: ExpenseCategory;
  label: string;
  /** Palette key (see TONE_CLASS in pages/Finance.tsx), or null for default. */
  tone: string | null;
  sort_order: number;
  /** Hidden from the pickers, but still resolves on historical rows. */
  archived: boolean;
  /** Set on the ten seeded buckets; null for user-created ones. */
  system_key: string | null;
  /** The salary buckets — renameable, but not archivable or deletable. */
  protected: boolean;
  expense_count: number;
  recurring_count: number;
}

export type ExpenseCurrency = "USD" | "GEL" | "EUR";

export interface Expense {
  id: string;
  category: ExpenseCategory;
  amount: string;
  currency: string;
  amount_gel: string | null;
  expense_date: string;
  period_month: string | null;
  description: string | null;
  vendor: string | null;
  source: "manual" | "recurring";
  recurring_id: string | null;
  created_by_user_id: string | null;
  deal_id: string | null;
  payee_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpenseListResponse {
  items: Expense[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExpenseRecurring {
  id: string;
  name: string;
  category: ExpenseCategory;
  amount: string;
  currency: string;
  day_of_month: number;
  active: boolean;
  start_month: string;
  end_month: string | null;
  vendor: string | null;
  note: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanceCategoryTotal {
  category: ExpenseCategory;
  total_gel: string;
  count: number;
}

/** `salary_gel` is the agent_salary + team_lead_salary slice OF `expenses_gel`,
 *  not an extra cost on top — so `net_income_gel - net_expense_gel === net_gel`.
 *  Income-derived figures are null when a month's FX was incomplete; the
 *  expense side is always reported and may be partial (see `fx_complete`). */
export interface FinanceMonthPoint {
  month: string; // "YYYY-MM"
  income_gel: string | null;
  expenses_gel: string;
  salary_gel: string;
  net_gel: string | null; // income − expenses
  net_income_gel: string | null; // income − salary payouts
  net_expense_gel: string; // expenses − salary payouts
  fx_complete: boolean;
}

export interface FinanceCurrencyAmount {
  currency: string;
  total: string;
}

export interface FinanceSummary {
  months: FinanceMonthPoint[];
  income_gel: string | null;
  expenses_gel: string;
  salary_gel: string;
  net_gel: string | null;
  net_income_gel: string | null;
  net_expense_gel: string;
  income_by_currency: FinanceCurrencyAmount[];
  expenses_by_category: FinanceCategoryTotal[];
  fx_complete: boolean;
}

/** One line of the finance ledger. `income` entries are derived live from
 *  closed deals — they have no expense row and are never editable here. */
export interface FinanceLogEntry {
  kind: "income" | "expense";
  /** Deal id for income, expense id for expenses — key on `kind:id`. */
  id: string;
  date: string;
  direction: "in" | "out";
  amount: string;
  currency: string;
  /** null when FX was unavailable — render the native amount, not a blank. */
  amount_gel: string | null;
  title: string;
  subtitle: string | null;
  category: ExpenseCategory | null;
  source: "manual" | "recurring" | null;
  deal_id: string | null;
  deal_title: string | null;
  payee_user_id: string | null;
  payee_name: string | null;
  agent_name: string | null;
  editable: boolean;
  /** The deal id on lines belonging to a deal group, null on standalone lines.
   *  Grouped lines arrive adjacent and in group order, so the page forms
   *  groups by walking `items` once. */
  group_key: string | null;
}

/** One deal group's header: the commission earned and what was paid out of it. */
export interface FinanceLogGroup {
  deal_id: string;
  deal_title: string;
  /** The close date — used to mark a commission booked in an earlier month. */
  deal_date: string;
  client_name: string | null;
  agent_name: string | null;
  commission: string;
  currency: string;
  commission_gel: string | null;
  /** False when the commission is not one of this page's lines (the deal closed
   *  earlier, or the filter excluded income) — render it as a greyed context
   *  line built from the fields above. */
  commission_line_included: boolean;
  /** ALL-TIME salary booked against the deal, not just this month — a payout is
   *  routinely booked later than the close. `complete` is false when a
   *  contributing row had no GEL value, making the total a floor. */
  paid_out_gel: string;
  paid_out_complete: boolean;
  /** True when that total reaches outside the visible month. */
  paid_out_beyond_month: boolean;
  /** commission_gel − paid_out_gel; null when either side is partial. */
  kept_gel: string | null;
}

export interface FinanceLogResponse {
  items: FinanceLogEntry[];
  groups: FinanceLogGroup[];
  /** Counts TOP-LEVEL UNITS (deal groups + standalone lines), not lines. */
  total: number;
  /** The raw line total, for the footer. */
  line_count: number;
  limit: number;
  offset: number;
  fx_complete: boolean;
  fx_missing: number;
  truncated: boolean;
}

export interface FinanceUserRef {
  id: string;
  name: string;
  /** Shown beside the name in the payee picker — a suggested team lead may be
   *  an actual lead broker or a higher-up manager. */
  role: string | null;
}

/** A closed deal a salary payout can point at, with the context the modal
 *  shows: what the deal earned and what's already been paid against it. */
export interface FinanceDealRef {
  id: string;
  deal_date: string;
  title: string;
  client_name: string | null;
  commission: string;
  currency: string;
  commission_gel: string | null;
  agent: FinanceUserRef | null;
  /** An agent may have several lead brokers, or none. */
  team_lead_candidates: FinanceUserRef[];
  paid_out_gel: string;
  paid_out_complete: boolean;
}

export interface FinanceDealListResponse {
  items: FinanceDealRef[];
}

export interface ExpenseAuditEntry {
  id: string;
  action: string;
  expense_id: string | null;
  recurring_id: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}
